// @flow

import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useForm, useWatch } from 'react-hook-form';
import { toastr } from 'react-redux-toastr';
import {
  formatCurrency,
  formatDateDB,
} from '@fortress-technology-solutions/fortress-component-library/utils';

import useIntl from '../../../hooks/useIntl';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import useRd356010FormQuery from '../../../hooks/data-fetching/useRd356010FormQuery';
import useGenerateRd356010Mutation from '../../../hooks/data-modifying/useGenerateRd356010Mutation';
import {
  formatCalendarDateUtc,
  formatDateLocale,
} from '../../../utils/date-helpers';
import messages from './messages';
import {
  buildGridLayout,
  findAgingMismatches,
  periodProblem,
  toFormValues,
  toGeneratePayload,
  totalsToUpdate,
} from './utils';
import type {
  Customize356010ModalViewModel,
  GridLayout,
  Rd356010Form,
  Rd356010Period,
} from './types';

// The api helpers reject with a string, `{ networkError }` or an object with
// `error` / `message`, not always an Error.
const toErrorText = (error: mixed): string => {
  if (typeof error === 'string') {
    return error;
  }
  const value: Object = error ?? {};
  return String(
    value.error ?? value.message ?? value.networkError ?? '',
  ).trim();
};

const EMPTY_LAYOUT: GridLayout = { sections: [], fieldsByLineId: {} };

// Calendar days (YYYY-MM-DD), never points in time: a moment such as 23:59 on
// Dec 31 turns into Jan 1 once formatted in UTC west of Greenwich.
type Period = { beginDate: string, endDate: string };

const PERIOD_MESSAGES = {
  invalid: messages.periodInvalid,
  order: messages.periodOrder,
  length: messages.periodLength,
};

const thisCalendarYear = (): Period => {
  const year = moment().year();
  return { beginDate: `${year}-01-01`, endDate: `${year}-12-31` };
};

const toPickerValue = (day: string): Object => moment(day, 'YYYY-MM-DD', true);

// The prior year the backend loads (Report356010.resolvePeriods): the same
// dates a year earlier, except that a period of whole months (a 1st to a
// month-end) keeps whole months, so a Feb 28 end becomes Feb 29 in a leap year.
export const priorPeriod = (current: Period): Period => {
  const begin = moment(current.beginDate, 'YYYY-MM-DD', true);
  const end = moment(current.endDate, 'YYYY-MM-DD', true);
  const priorEnd = end.clone().subtract(1, 'year');
  if (begin.date() === 1 && end.isSame(end.clone().endOf('month'), 'day')) {
    priorEnd.endOf('month');
  }
  return {
    beginDate: formatDateLocale(begin.clone().subtract(1, 'year')),
    endDate: formatDateLocale(priorEnd),
  };
};

export const useCustomize356010Modal = ({
  onClose,
}: {
  onClose: () => void,
}): Customize356010ModalViewModel => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty() ?? {};
  const organizationId = selectedProperty.organizationId ?? '';
  const propertyId = selectedProperty.id ?? '';
  const propertyName = selectedProperty.name ?? '';

  // The current-year period picked on screen; the prior year is the same
  // dates a year earlier.
  const [period, setPeriod] = useState<Period>(thisCalendarYear);
  // The period whose Intacct data is loaded; changes only on Refresh.
  const [loadedPeriod, setLoadedPeriod] = useState<Period>(thisCalendarYear);
  const problem = periodProblem(period.beginDate, period.endDate);

  const query = useRd356010FormQuery({
    organizationId,
    propertyId,
    currentYearBeginDate: loadedPeriod.beginDate,
    currentYearEndDate: loadedPeriod.endDate,
  });
  // The form being edited. Only the first load and an explicit Refresh replace
  // it: the app invalidates every query when a page comes back from the
  // back/forward cache, and that refetch must not wipe the user's edits. The
  // snapshot belongs to one property; browser history can switch the selected
  // property without remounting this modal, and another property's balances
  // must never be generated under this one.
  const acceptNextLoad = useRef(true);
  const [loaded, setLoaded] = useState<{
    form: ?Rd356010Form,
    at: number,
    propertyId: string,
  }>({ form: null, at: 0, propertyId });
  const form = loaded.propertyId === propertyId ? loaded.form : null;
  useEffect(() => {
    acceptNextLoad.current = true;
  }, [propertyId]);
  useEffect(() => {
    if (query.data && acceptNextLoad.current) {
      acceptNextLoad.current = false;
      setLoaded({ form: query.data, at: query.dataUpdatedAt, propertyId });
    }
  }, [query.dataUpdatedAt]);
  // A failed Refresh keeps the current edits, so a later refetch nobody asked
  // for must not replace them either.
  useEffect(() => {
    if (query.isError && form) {
      acceptNextLoad.current = false;
    }
  }, [query.errorUpdatedAt]);

  const useFormReturn = useForm({ mode: 'onChange' });
  const { control, getValues, handleSubmit, reset, setValue } = useFormReturn;
  const values = useWatch({ control });

  const layout = useMemo(
    () =>
      form
        ? buildGridLayout(form.lines, {
            assets: intl.formatMessage(messages.assets),
            liabilities: intl.formatMessage(messages.liabilities),
          })
        : EMPTY_LAYOUT,
    [form],
  );

  // Every accepted load (first open or Refresh) replaces whatever was edited.
  useEffect(() => {
    if (form) {
      reset(toFormValues(form, layout));
    }
  }, [loaded.at]);

  // Live totals: read-only cells recomputed whenever a detail cell changes.
  useEffect(() => {
    if (!form) {
      return;
    }
    totalsToUpdate(form.lines, layout, getValues()).forEach(
      ({ fieldName, value }) => {
        setValue(fieldName, value, { shouldDirty: false });
      },
    );
  }, [values, form, layout]);

  const columnLabels = useMemo(
    () => ({
      currentYear: intl.formatMessage(messages.currentYear),
      priorYear: intl.formatMessage(messages.priorYear),
    }),
    [],
  );

  const agingWarnings = form
    ? findAgingMismatches(form.lines, layout, values ?? {}).map((mismatch) =>
        intl.formatMessage(messages.agingMismatch, {
          lineId: mismatch.lineId,
          column: columnLabels[mismatch.column],
          sublineTotal: formatCurrency(mismatch.sublineTotal),
          lineTotal: formatCurrency(mismatch.lineTotal),
        }),
      )
    : [];

  const formatPeriod = (range: ?Rd356010Period): string =>
    range
      ? intl.formatMessage(messages.period, {
          beginDate: formatCalendarDateUtc(range.beginDate) ?? '',
          endDate: formatCalendarDateUtc(range.endDate) ?? '',
        })
      : '';
  const currentYearRange = formatPeriod(form?.header.currentYear);
  const priorYearRange = formatPeriod(form?.header.priorYear);

  const columns = useMemo(
    () => [
      {
        label: intl.formatMessage(messages.line),
        flexSize: 2,
        textAlign: 'left',
        sticky: true,
      },
      {
        label: intl.formatMessage(messages.identify),
        key: 'identifyLabel',
        flexSize: 1,
        textAlign: 'left',
        type: 'text',
      },
      {
        label: columnLabels.currentYear,
        subLabel: currentYearRange,
        key: 'currentYear',
        flexSize: 1,
        type: 'number-currency',
      },
      {
        label: columnLabels.priorYear,
        subLabel: priorYearRange,
        key: 'priorYear',
        flexSize: 1,
        type: 'number-currency',
      },
      {
        label: intl.formatMessage(messages.comments),
        key: 'comment',
        flexSize: 1,
        textAlign: 'left',
        placeholder: intl.formatMessage(messages.commentPlaceholder),
        type: 'text',
      },
    ],
    [currentYearRange, priorYearRange],
  );

  const generateMutation = useGenerateRd356010Mutation({
    organizationId,
    propertyId,
    propertyName,
    options: {
      onSuccess: () => {
        toastr.success(
          intl.formatMessage(messages.success),
          intl.formatMessage(messages.generateSuccess),
        );
      },
      onError: (error) => {
        toastr.error(
          intl.formatMessage(messages.error),
          intl.formatMessage(messages.generateError, {
            error: toErrorText(error),
          }),
        );
      },
    },
  });

  const onRefresh = () => {
    if (problem) {
      return;
    }
    const requested = period;
    acceptNextLoad.current = true;
    if (
      requested.beginDate === loadedPeriod.beginDate &&
      requested.endDate === loadedPeriod.endDate
    ) {
      query.refetch();
    } else {
      setLoadedPeriod(requested);
    }
  };

  // Generate prints the period of the form on screen, so the pickers must show
  // that period. Compared with the form itself, not the last request: a
  // Refresh that failed leaves the previous form in place.
  const isPeriodLoaded =
    period.beginDate === form?.header.currentYear.beginDate &&
    period.endDate === form?.header.currentYear.endDate;

  const priorYearPreview = problem
    ? ''
    : intl.formatMessage(messages.priorYearPreview, {
        period: formatPeriod(priorPeriod(period)),
      });

  const onGenerate = handleSubmit((formValues) => {
    if (form) {
      generateMutation.mutate(toGeneratePayload(form, layout, formValues));
    }
  });

  const isLoading = query.isLoading || query.isFetching;

  return {
    title: [intl.formatMessage(messages.title), propertyName]
      .filter(Boolean)
      .join(' - '),
    currentYearBegin: toPickerValue(period.beginDate),
    currentYearEnd: toPickerValue(period.endDate),
    // The picker's value becomes a calendar day the way the app's other
    // date pickers do it (Forms/DatePickerCL).
    onCurrentYearBeginChange: (date) =>
      setPeriod((current) => ({
        ...current,
        beginDate: date ? formatDateDB(date) : thisCalendarYear().beginDate,
      })),
    onCurrentYearEndChange: (date) =>
      setPeriod((current) => ({
        ...current,
        endDate: date ? formatDateDB(date) : thisCalendarYear().endDate,
      })),
    priorYearPreview,
    periodError: problem ? intl.formatMessage(PERIOD_MESSAGES[problem]) : '',
    periodNotice:
      problem || isPeriodLoaded
        ? ''
        : intl.formatMessage(messages.periodNotLoaded),
    form,
    isLoading,
    isError: query.isError,
    errorMessage: intl.formatMessage(messages.loadError, {
      error: toErrorText(query.error),
    }),
    useFormReturn,
    columns,
    sections: layout.sections,
    agingWarnings,
    currentYearRange,
    priorYearRange,
    isGenerating: generateMutation.isLoading,
    canGenerate:
      Boolean(form) && !isLoading && !query.isError && isPeriodLoaded,
    canRefresh: !problem,
    onRefresh,
    onGenerate,
    onClose,
  };
};
